import { formatMoney, formatNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { calculateCPM, calculateCPX } from '@src/utils/metric-utils';
import { equalsIgnoreCase } from '@src/utils/text-utils';
import {
  AgeRange,
  AudienceInsightsDimensions,
  GoogleAudience
} from '@blisspointmedia/bpm-types/dist/YoutubePlanning';
import { AGE_KEYS } from './youtube-planning-constants';
import {
  AudienceInsightMenuItem,
  PrefillInterest,
  YouTubePlanTableData,
  YouTubeProductSpecificForecasts
} from './youtube-planning-types';

// Human-friendly axis ticks across [min, max], stepping by 1, 2 or 5 times a power of ten.
//
// Moved here verbatim from SpendVsChart.tsx; the arithmetic is unchanged. Note a degenerate range
// (max <= min) or a non-finite bound sends roughStep through Math.log10 to NaN and yields an empty
// tick array. That is existing behavior and is deliberately left as-is -- the caller's job is not
// to chart an empty series in the first place, which SpendVsChart now handles by rendering an
// empty state instead of mounting the chart.
export const getNiceTicks = (min: number, max: number, tickCount = 5): number[] => {
  const roughStep = (max - min) / tickCount;
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const residual = roughStep / magnitude;

  let niceStep;
  if (residual >= 5) {
    niceStep = 5 * magnitude;
  } else if (residual >= 2) {
    niceStep = 2 * magnitude;
  } else {
    niceStep = magnitude;
  }

  const niceMin = Math.floor(min / niceStep) * niceStep;

  const ticks: number[] = [];
  for (let i = niceMin; i <= max; i += niceStep) {
    ticks.push(i);
  }
  if (ticks[ticks.length - 1] < max) {
    ticks.push(max);
  }
  return ticks;
};

export const mapToAudienceInsightMenuItem = (
  audiences: GoogleAudience[]
): AudienceInsightMenuItem[] =>
  audiences.map(audience => ({
    dimension: audience.dimension,
    label: audience.name,
    value: audience.id
  }));

// The search result for a copied interest: the result with its own id, else a category with the
// same name. Other results with the same name can be a different entity (e.g. a film genre).
export const findSearchedInterest = (
  items: AudienceInsightMenuItem[],
  { id, label }: PrefillInterest
): AudienceInsightMenuItem | undefined => {
  const sameId = items.find(item => item.value === id);
  if (sameId) return sameId;
  return items.find(
    item =>
      item.dimension === AudienceInsightsDimensions.CATEGORY && equalsIgnoreCase(item.label, label)
  );
};

export const mapToGoogleAudience = (items: AudienceInsightMenuItem[]): GoogleAudience[] =>
  items
    .filter(item => item.dimension != null && item.label != null && item.value != null)
    .map(item => ({
      dimension: item.dimension!,
      name: item.label!,
      id: item.value
    }));

export const filterDimensionListFromCombined = (
  combined: AudienceInsightMenuItem[],
  dimension: AudienceInsightsDimensions
): AudienceInsightMenuItem[] =>
  combined
    .filter(segment => segment.dimension === dimension && segment.label != null)
    .sort((a, b) => a.label!.localeCompare(b.label ?? '', 'en'));

export const isAgeRangeArrConsecutive = (ageRangeArr: (keyof typeof AgeRange)[]): boolean => {
  const indices = ageRangeArr.map(range => AGE_KEYS.indexOf(range)).sort((a, b) => a - b);
  for (let i = 1; i < indices.length; i++) {
    if (indices[i] - indices[i - 1] > 1) return false;
  }
  return true;
};

export const parseNumberFromMoney = (moneyStr: string): number => {
  const cleaned = moneyStr.replace(/[^\d.]/g, '');
  return parseFloat(cleaned);
};

export const buildTableRow = (
  forecast: YouTubeProductSpecificForecasts,
  suggestedBudget: number,
  id: number,
  format: string
): YouTubePlanTableData => {
  const cost = Number(forecast.costMicros) / 1000000;
  const onTargetReach = parseInt(forecast.plannedProductForecast.onTargetReach, 10);
  const onTargetImpressions = parseInt(forecast.plannedProductForecast.onTargetImpressions, 10);
  const views = parseInt(forecast.plannedProductForecast.views, 10);

  return {
    id,
    format,
    budget: formatMoney(cost, 0),
    budgetShare: `${Math.round((suggestedBudget ? cost / suggestedBudget : 0) * 100)}%`,
    reach: formatNumber(onTargetReach),
    avgFreq: formatNumber(onTargetReach ? onTargetImpressions / onTargetReach : 0),
    costPerReach: formatMoney(calculateCPX(cost, onTargetReach)),
    imps: formatNumber(onTargetImpressions),
    cpm: formatMoney(calculateCPM(cost, onTargetImpressions)),
    views: formatNumber(views),
    cpv: formatMoney(calculateCPX(cost, views))
  };
};
